import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Coins, ArrowLeftRight } from "lucide-react";
import { TxnForm, TransferForm, TxnList } from "@/components/CashShared";

function SumCard({ l, v, testid, accent }) {
  return (<div data-testid={testid} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"><div className="text-[10px] uppercase tracking-wide text-gray-400">{l}</div><div className={`mt-1 text-sm font-bold ${accent || "text-gray-800"}`}>{v}</div></div>);
}

export default function PettyCash() {
  const [masters, setMasters] = useState({ accounts: [], coa: [], costCenters: [], revenueCats: [], expenseCats: [], paymentMethods: [], suppliers: [] });
  const [account, setAccount] = useState("");
  const [summary, setSummary] = useState({});
  const [txns, setTxns] = useState([]);

  const loadMasters = useCallback(async () => {
    const g = (r) => api.get(`/master/${r}`, { params: { active: "true" } }).then((x) => x.data).catch(() => []);
    const [accounts, coa, costCenters, revenueCats, expenseCats, paymentMethods, suppliers] = await Promise.all([
      api.get("/cash/accounts").then((x) => (x.data || []).filter((a) => a.active !== false)).catch(() => []),
      g("coa"), g("cost_center"), g("revenue_category"), g("expense_category"), g("payment_method"), g("supplier"),
    ]);
    setMasters({ accounts, coa: coa.filter((c) => c.is_posting !== false), costCenters, revenueCats, expenseCats, paymentMethods, suppliers });
    const petty = accounts.filter((a) => a.is_petty_cash);
    if (!account && petty.length) setAccount(petty[0].id);
  }, [account]);

  const loadData = useCallback(async () => {
    if (!account) return;
    try {
      const [{ data: s }, { data: t }] = await Promise.all([
        api.get(`/cash/account-summary/${account}`),
        api.get("/cash/transactions", { params: { account_id: account } }),
      ]);
      setSummary(s || {}); setTxns(t || []);
    } catch (e) { /* ignore */ }
  }, [account]);

  useEffect(() => { loadMasters(); }, [loadMasters]);
  useEffect(() => { loadData(); }, [loadData]);

  const refresh = () => loadData();
  const pettyAccounts = masters.accounts.filter((a) => a.is_petty_cash);
  const pettyAccount = masters.accounts.find((a) => a.id === account);

  return (
    <div>
      <h1 className="font-heading text-xl font-bold text-[#111827] sm:text-2xl"><Coins size={26} className="mr-2 inline text-[#3B82F6]" />Petty Cash</h1>
      <p className="mt-1 text-sm text-gray-500">Kas kecil memakai akun Cash & Bank dari Master Data · setiap transaksi memengaruhi saldo akun terkait</p>

      <div className="mt-4">
        <Select value={account} onValueChange={setAccount}>
          <SelectTrigger data-testid="petty-account-select" className="h-10 max-w-sm"><SelectValue placeholder="Pilih Petty Cash Account" /></SelectTrigger>
          <SelectContent>{pettyAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_name} ({a.type})</SelectItem>)}</SelectContent>
        </Select>
        {pettyAccounts.length === 0 && <p className="mt-2 text-xs text-amber-700">Belum ada akun Petty Cash. Tandai akun sebagai Petty Cash di Master Data → Cash & Bank Account.</p>}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <SumCard l="Opening Balance" v={formatRupiah(summary.opening_balance)} testid="petty-sum-opening" />
        <SumCard l="Cash In" v={formatRupiah(summary.cash_in)} testid="petty-sum-in" accent="text-emerald-700" />
        <SumCard l="Cash Out" v={formatRupiah(summary.cash_out)} testid="petty-sum-out" accent="text-red-600" />
        <SumCard l="Current Balance" v={formatRupiah(summary.current_balance)} testid="petty-sum-balance" accent="text-[#8a6d1a]" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <TxnForm mode="out" masters={masters} fixedAccountId={account} onDone={refresh}
          trigger={<Button data-testid="petty-expense-open" size="sm" className="bg-red-600 text-white hover:bg-red-700"><Coins size={15} className="mr-1" />Petty Cash Expense</Button>} />
        <TransferForm masters={masters} fixedToId={account} onDone={refresh} title="Petty Cash Refill (Transfer)"
          trigger={<Button data-testid="petty-refill-open" size="sm" variant="outline" className="border-gray-300"><ArrowLeftRight size={15} className="mr-1" />Refill</Button>} />
      </div>

      {pettyAccount && <p className="mt-4 text-xs text-gray-400">Akun: <span className="font-semibold text-gray-600">{pettyAccount.account_name}</span> · Refill dicatat sebagai transfer antar akun (bukan pendapatan).</p>}

      <TxnList txns={txns} onVoid={refresh} showBalance />
    </div>
  );
}
